import { ChangeDetectionStrategy, Component, input } from '@angular/core';

import { StatusBadgeComponent } from '../status-badge.component';
import type { AutomaticCheckRow } from './automatic-checks.types';

/**
 * "Automatische controles" card: one label/value row per automatic check (KvK, RDW), each with a
 * status (badge or headline) and a muted detail line. Presentational — the rows arrive resolved.
 *
 * Card chrome lives in component CSS rather than Tailwind utilities on purpose: Tailwind runs with
 * `important: true`, so a utility could not be undone by the `.bare` rule below — and for the same
 * reason text colours are `.tone-*` classes, not Tailwind colour utilities. `bare` drops the chrome
 * for hosts that already draw a card with a title (the Formly field on a detail page: the Overzicht
 * card or the tab's section card); without it the view is a standalone card (Storybook).
 */
@Component({
  selector: 'dd-automatic-checks-view',
  imports: [StatusBadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }
    .card {
      border: 1px solid var(--dd-card-border);
      border-radius: 0.75rem;
      padding: 1.5rem;
      background: var(--fuse-bg-card);
    }
    .row {
      padding-block: 1rem;
    }
    .row + .row {
      border-top: 1px solid var(--dd-card-border);
    }
    .row:last-child {
      padding-bottom: 0;
    }
    .tone-default {
      color: var(--fuse-text-default);
    }
    .tone-muted {
      color: var(--dd-card-muted-fg);
    }
    .tone-negative {
      color: var(--dd-badge-negative-fg);
    }
    .card.bare {
      border: 0;
      border-radius: 0;
      padding: 0;
      background: transparent;
    }
    .card.bare .row:first-of-type {
      padding-top: 0;
    }
  `,
  template: `
    <section class="card" [class.bare]="bare()" data-cy="automatic-checks">
      @if (title() && !bare()) {
        <h3 class="title m-0 mb-2 text-[0.9375rem] font-semibold leading-[1.3] text-default">{{ title() }}</h3>
      }
      @for (row of rows(); track row.label) {
        <div
          class="row flex flex-col gap-1 sm:flex-row sm:gap-4"
          [attr.aria-busy]="row.loading ? 'true' : null"
          [attr.data-cy]="'automatic-check-' + row.label">
          <span class="tone-muted text-[0.9375rem] leading-[1.4] sm:w-32 sm:shrink-0">{{ row.label }}</span>
          <div class="flex min-w-0 flex-col gap-1">
            @if (row.loading) {
              <!-- Line boxes with the headline/detail line heights, so the row does not jump when
                   the text arrives. -->
              <span class="flex h-[21px] items-center" aria-hidden="true">
                <span class="block h-3.5 w-60 max-w-full animate-pulse rounded bg-[var(--dd-surface-subdued)]"></span>
              </span>
              <span class="flex h-[18px] items-center" aria-hidden="true">
                <span class="block h-3 w-80 max-w-full animate-pulse rounded bg-[var(--dd-surface-subdued)]"></span>
              </span>
              <span class="sr-only">{{ row.loadingLabel }}</span>
            } @else {
              @if (row.badge; as badge) {
                <app-status-badge
                  class="self-start"
                  size="md"
                  [label]="badge.label"
                  [variant]="badge.variant ?? 'neutral'"
                  [customColors]="badge.customColors ?? null"
                  [dot]="true" />
              } @else if (row.headline) {
                <span class="text-[0.9375rem] leading-[1.4]" data-cy="automatic-check-headline">
                  @for (part of row.headline; track $index) {
                    <span [class]="'tone-' + (part.tone ?? 'default')" [class.font-medium]="part.strong">{{
                      part.text
                    }}</span>
                  }
                </span>
              }
              @if (row.detail) {
                <span class="text-[0.8125rem] leading-[1.4]" data-cy="automatic-check-detail">
                  @for (part of row.detail; track $index) {
                    <span [class]="'tone-' + (part.tone ?? 'muted')" [class.font-medium]="part.strong">{{ part.text }}</span>
                  }
                </span>
              }
            }
          </div>
        </div>
      }
    </section>
  `,
})
export class AutomaticChecksViewComponent {
  readonly title = input('');
  readonly rows = input<AutomaticCheckRow[]>([]);
  /** Drop card chrome and title: the host already draws a card with its own heading. */
  readonly bare = input(false);
}
